// client/src/pages/MyMoonbeams.js
//
// User's Moonbeam library page.
//
// CHANGES IN THIS REVISION (Share modal + eCard→Moonbeam rename):
//   - The post-share success TOAST is now an explicit, centered, close-only
//     MODAL (ShareSuccessModal). Per the spec, it does NOT auto-dismiss —
//     the user must press an explicit Close/Done button. Three variants,
//     keyed off hasLink / hasEmail:
//       • Link only  → "Your link is copied to your clipboard," the URL in
//         a read-only field with a YouTube-style [Copy] button beside it,
//         plus an explicit Close.
//       • Email only → "Your card has been emailed," no link, plus the
//         spam/promotions warning and the nudge to tell the recipient.
//       • Both        → the link (with Copy) AND "also emailed," plus the
//         spam note.
//     The clipboard write already happened in MoonbeamCreator.handleSend,
//     so "copied" is truthful; the modal's Copy button re-copies (reusing
//     the same clipboard-with-legacy-fallback the old toast used) in case
//     the clipboard was overwritten between the creator and here.
//   - Display text "eCard(s)"→"Moonbeam(s)" in hardcoded UI strings
//     ("My Moonbeams," the delivery-tip banner, the delete confirm, the
//     native-share title/text). Routes, fields, variables, recipientKey,
//     and the ShareableLinkRow component are unchanged.
//
// CHANGES IN PREVIOUS REVISION (Shareable Links & Reactions build):
//   - Per-card Analytics button REMOVED. Engagement metrics (reactions,
//     views) now display inline for every user. Full analytics lives on
//     its own page (/account/analytics) for Business Wizard users.
//   - Card row summary shows inline reaction count.
//   - Shareable links section nested under each card row.
//   - "Shared" filter tab for link-only cards.
//
// CHANGES IN PREVIOUS REVISION (Dashboard build):
//   - "Dashboard" back button at top of page.

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import moonbeamService from '../services/moonbeamService.js';
import Button from '../components/ui/Button.js';
import Pagination from '../components/ui/Pagination.js';
import ViewRecipientsPanel from '../components/moonbeams/ViewRecipientsPanel.js';
import ShareableLinkRow from '../components/moonbeams/ShareableLinkRow.js';

// ─── LEGACY: Per-card analytics drawer ────────────────────────────────────
// The per-card analytics drawer was removed in the Shareable Links & Reactions
// build (May 2026). MoonbeamAnalytics.js still exists in the codebase as dead
// code. To bring back per-card analytics, uncomment the import below and mount
// it conditionally on a row in the moonbeams.map() block.
//
// import MoonbeamAnalytics from '../components/moonbeams/MoonbeamAnalytics.js';
// ──────────────────────────────────────────────────────────────────────────

import { formatDateTime, getStatusColor } from '../utils/formatters.js';
import { useAuth } from '../hooks/useAuth.js';
import { buildShareSummary } from '../components/moonbeams/MoonbeamCreatorShared.js';

// ─── Success Modal ──────────────────────────────────────────────────────────────
//
// Explicit, centered, CLOSE-ONLY modal shown after a share completes. Driven
// by the structured `shareSuccess` descriptor that MoonbeamCreator passes via
// navigation state. Unlike the old toast, this does NOT auto-dismiss — the
// user must press Close/Done. Three variants keyed off hasLink / hasEmail:
//
//   • Link only  → "Your link is copied to your clipboard" + URL field with
//                  a YouTube-style [Copy] button + Close.
//   • Email only → "Your card has been emailed" + spam/promotions warning +
//                  "tell your recipient" nudge + Close. No link shown.
//   • Both       → the link (with Copy) AND "also emailed" + spam note + Close.
//
// The clipboard write already happens in MoonbeamCreator.handleSend, so
// "copied" is truthful on landing. The Copy button re-copies (reusing the
// same clipboard-with-legacy-fallback the old toast used) in case the
// clipboard was overwritten between the creator and here.
function ShareSuccessModal({ shareSuccess, onClose }) {
  const [copyFeedback, setCopyFeedback] = useState(null);

  const {
    linkUrl, linkLabel,
    emailRecipientCount, emailDeliveryMode, emailScheduleDate, emailBirthdayTime,
  } = shareSuccess;

  const hasLink = !!linkUrl;
  const hasEmail = !!emailRecipientCount && emailRecipientCount > 0;

  // Per-channel narration, reusing the same summary builder the creator's
  // Step 2 uses so the modal wording matches what the user saw before they
  // pressed Share. Single source of truth for the share narration.
  const summaryLines = buildShareSummary({
    linkEnabled: hasLink,
    linkLabel: linkLabel || '',
    emailRecipientCount: hasEmail ? emailRecipientCount : 0,
    deliveryMode: emailDeliveryMode || 'now',
    scheduleDate: emailScheduleDate || null,
    birthdayTime: emailBirthdayTime || null,
  });

  // Heading varies based on what happened.
  let heading = 'Done!';
  if (hasLink && hasEmail) heading = '✨ Done! Two things just happened';
  else if (hasLink) heading = '✨ Your link is ready';
  else if (hasEmail && emailDeliveryMode === 'now') heading = '🎉 Your card has been emailed';
  else if (hasEmail && emailDeliveryMode === 'schedule') heading = '📅 Your card is scheduled';
  else if (hasEmail && emailDeliveryMode === 'birthday') heading = '🎂 Birthday auto-send activated';

  // Native share button when on mobile + browser supports it.
  const canNativeShare = hasLink && typeof navigator !== 'undefined' && !!navigator.share;

  const handleCopy = async () => {
    if (!linkUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(linkUrl);
        setCopyFeedback('Copied!');
      } else {
        // Legacy fallback for non-secure contexts.
        const ta = document.createElement('textarea');
        ta.value = linkUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopyFeedback('Copied!');
      }
    } catch {
      setCopyFeedback('Copy failed');
    }
    setTimeout(() => setCopyFeedback(null), 1800);
  };

  const handleNativeShare = async () => {
    if (!linkUrl) return;
    try {
      await navigator.share({
        title: 'Your Moonbeam',
        text: 'I made you a Moonbeam — take a look:',
        url: linkUrl,
      });
    } catch {
      // User cancelled the share sheet, or share failed silently.
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Share complete"
    >
      <div className="bg-cream dark:bg-royal-900 border border-royal-200 dark:border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-royal-200/60 dark:border-royal-700/50 flex-shrink-0">
          <h2 className="font-display text-xl sm:text-2xl text-gold-dark dark:text-gold">
            {heading}
          </h2>
          <button
            onClick={onClose}
            className="text-royal-500 dark:text-royal-200 hover:text-royal-900 dark:hover:text-white transition-colors p-1 -mr-1 flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-4">

          {/* Per-channel summary lines, mirroring the Step 2 review. */}
          {summaryLines.length > 0 && (
            <ul className="space-y-2">
              {summaryLines.map((line, i) => (
                <li key={i} className="flex items-start gap-2.5 text-royal-800 dark:text-royal-100 text-base">
                  <span className="flex-shrink-0 mt-0.5" aria-hidden="true">{line.icon}</span>
                  <span className="leading-relaxed">{line.text}</span>
                </li>
              ))}
            </ul>
          )}

          {/* ── LINK BLOCK ──────────────────────────────────────────────────
              YouTube-style: read-only URL field + Copy button beside it. */}
          {hasLink && (
            <div className="space-y-2">
              <p className="text-royal-700 dark:text-royal-200 text-sm font-medium">
                📋 Your link is copied to your clipboard — paste it anywhere
                (text, WhatsApp, Facebook, Instagram).
              </p>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={linkUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 px-3 py-2.5 bg-white dark:bg-royal-800/60 border border-royal-300 dark:border-royal-600 rounded-lg text-royal-900 dark:text-white text-sm focus:outline-none focus:border-gold/60 transition-colors"
                  aria-label="Shareable link URL"
                />
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
                >
                  {copyFeedback || 'Copy'}
                </button>
              </div>

              {canNativeShare && (
                <button
                  onClick={handleNativeShare}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-royal-300 dark:border-royal-600 text-royal-700 dark:text-royal-100 rounded-lg text-sm font-medium hover:border-gold/50 hover:text-gold transition-colors"
                >
                  <span aria-hidden="true">📤</span>
                  <span>Share now via app</span>
                </button>
              )}
            </div>
          )}

          {/* ── EMAIL NOTICE ────────────────────────────────────────────────
              Spam/promotions warning + tell-your-recipient nudge. Shown
              whenever email was part of the share (email-only OR both). */}
          {hasEmail && (
            <div className="p-3.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/40 rounded-lg space-y-2">
              <p className="text-amber-900 dark:text-amber-200 text-sm font-semibold">
                💡 Heads up about email delivery
              </p>
              <p className="text-amber-800 dark:text-amber-300/90 text-sm leading-relaxed">
                Email systems sometimes tuck cards into <strong>Spam</strong> or{' '}
                <strong>Promotions</strong>. It's a good idea to let your recipient
                know you sent them a card so they can look for it — and move it to
                their inbox if it landed in the wrong folder.
              </p>
            </div>
          )}
        </div>

        {/* Footer — explicit close. */}
        <div className="px-5 sm:px-6 py-4 border-t border-royal-200/60 dark:border-royal-700/50 flex-shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delivery Tip Banner ──────────────────────────────────────────────────────
// Persistent banner that helps users understand email deliverability. Also
// surfaces the shareable-link option as a way to skip email entirely.
// Dismissable per session — reappears on next visit.
function DeliveryTipBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('deliveryTipDismissed') === 'true';
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('deliveryTipDismissed', 'true');
    } catch {
      // sessionStorage not available, just hide for this render
    }
  };

  if (dismissed) return null;

  return (
    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700/40 rounded-xl">
      <div className="flex items-start gap-3">
        <span className="text-xl flex-shrink-0 mt-0.5">📬</span>
        <div className="flex-1">
          <p className="text-amber-900 dark:text-amber-200 font-semibold text-sm mb-1">
            Help your Moonbeams get delivered
          </p>
          <p className="text-amber-800 dark:text-amber-300/80 text-sm leading-relaxed mb-2">
            <strong>For email sends:</strong> tell recipients to check <strong className="font-semibold">Spam</strong> or <strong className="font-semibold">Junk</strong> if they don't see your card. Moving it to their inbox helps future Moonbeams land safely.
          </p>
          <p className="text-amber-800 dark:text-amber-300/80 text-sm leading-relaxed">
            <strong>Or skip email entirely</strong> — generate a shareable link when you create your next card and paste it anywhere you want (text, WhatsApp, Facebook).
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors p-1 flex-shrink-0"
          title="Dismiss for this session"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Delivery Progress (for scheduled/birthday cards) ─────────────────────────
function DeliveryProgress({ sentCount, recipientCount, deliveryMode }) {
  if (!recipientCount || recipientCount === 0) return null;

  const sent = parseInt(sentCount) || 0;
  const total = parseInt(recipientCount) || 0;
  const pending = total - sent;

  if (pending === 0) return null;

  let icon = '📬';
  let colorClass = 'text-blue-400';

  if (deliveryMode === 'birthday') {
    icon = '🎂';
    colorClass = 'text-purple-400';
  } else if (deliveryMode === 'scheduled') {
    icon = '⏱️';
    colorClass = 'text-blue-400';
  }

  return (
    <p className={`text-sm mt-1 ${colorClass}`}>
      {icon} {sent} of {total} sent{pending > 0 ? ` · ${pending} pending` : ''}
    </p>
  );
}

// ─── Inline Reactions Summary ─────────────────────────────────────────────────
function ReactionsInlineSummary({ reactionCount }) {
  const total = parseInt(reactionCount) || 0;
  if (total === 0) return null;
  return (
    <span className="ml-1">
      · 💬 {total} reaction{total !== 1 ? 's' : ''}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MyMoonbeams() {
  const { user } = useAuth();
  const location = useLocation();
  const [moonbeams, setMoonbeams] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Panel state: only one View panel can be open at a time.
  const [openPanel, setOpenPanel] = useState(null);

  // Shareable links cache, keyed by moonbeamid. Lazily loaded.
  const [linksByMoonbeam, setLinksByMoonbeam] = useState({});

  // Share-success modal state from navigation. Reads the structured
  // descriptor from MoonbeamCreator and clears it from history so it
  // doesn't reappear on refresh.
  const [shareSuccess, setShareSuccess] = useState(null);

  useEffect(() => {
    if (location.state?.shareSuccess) {
      setShareSuccess(location.state.shareSuccess);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchMoonbeams = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (statusFilter) params.set('status', statusFilter);
      const data = await moonbeamService.getMyMoonbeams(params.toString());
      setMoonbeams(data.moonbeams);
      setTotalPages(data.totalPages);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchMoonbeams(); }, [page, statusFilter]);

  // Reset any stuck loading entries so the fetch effect can re-fetch them.
  useEffect(() => {
    setLinksByMoonbeam(prev => {
      const next = { ...prev };
      let changed = false;
      for (const id in next) {
        if (next[id]?.loading === true) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [moonbeams]); // eslint-disable-line react-hooks/exhaustive-deps

  // The actual fetch effect.
  useEffect(() => {
    const candidates = moonbeams.filter(m =>
      parseInt(m.link_count) > 0
      && !linksByMoonbeam[m.moonbeamid]
    );

    if (candidates.length === 0) return;

    setLinksByMoonbeam(prev => {
      const next = { ...prev };
      for (const m of candidates) {
        next[m.moonbeamid] = { loading: true, links: [], error: null };
      }
      return next;
    });

    Promise.allSettled(candidates.map(m =>
      moonbeamService.getShareableLinks(m.moonbeamid)
        .then(resp => ({ moonbeamid: m.moonbeamid, links: resp.links || [], error: null }))
        .catch(err => ({ moonbeamid: m.moonbeamid, links: [], error: err?.data?.error || 'Failed to load' }))
    )).then((results) => {
      setLinksByMoonbeam(prev => {
        const next = { ...prev };
        for (const r of results) {
          if (r.status === 'fulfilled') {
            next[r.value.moonbeamid] = { loading: false, links: r.value.links, error: r.value.error };
          }
        }
        return next;
      });
    });
  }, [moonbeams, linksByMoonbeam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    const linksForCard = linksByMoonbeam[id]?.links || [];
    const message = linksForCard.length > 0
      ? `Delete this Moonbeam? Its ${linksForCard.length} shareable link${linksForCard.length !== 1 ? 's' : ''} will also stop working.`
      : 'Delete this Moonbeam?';
    if (!window.confirm(message)) return;
    await moonbeamService.remove(id);
    if (openPanel?.id === id) setOpenPanel(null);
    setLinksByMoonbeam(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    fetchMoonbeams();
  };

  const toggleViewPanel = (id) => {
    if (openPanel?.type === 'view' && openPanel?.id === id) {
      setOpenPanel(null);
    } else {
      setOpenPanel({ type: 'view', id });
    }
  };

  const isViewPanelOpen = (id) => openPanel?.type === 'view' && openPanel?.id === id;

  const canShowViewButton = (status) =>
    status === 'sent' || status === 'scheduled' || status === 'shared';

  const handleLinkUpdated = (moonbeamid, updatedLink) => {
    setLinksByMoonbeam(prev => {
      const cur = prev[moonbeamid];
      if (!cur) return prev;
      const nextLinks = cur.links.map(l =>
        l.linkid === updatedLink.linkid ? { ...l, ...updatedLink } : l
      );
      return { ...prev, [moonbeamid]: { ...cur, links: nextLinks } };
    });
  };

  const dateLabel = (m) => {
    if (m.status === 'sent' && m.sentat) return `Sent ${formatDateTime(m.sentat)}`;
    if (m.status === 'shared') return `Shared ${formatDateTime(m.createdat)}`;
    return `Created ${formatDateTime(m.createdat)}`;
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Dashboard Back Link */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold">My Moonbeams</h1>
          <Link to="/moonbeams"><Button>Create New</Button></Link>
        </div>

        {/* Delivery Tip Banner — persistent, dismissable per session */}
        <DeliveryTipBanner />

        {/* Filter tabs — includes Shared for link-only cards */}
        <div className="flex gap-2 mb-6">
          {['', 'scheduled', 'sent', 'shared'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => { setStatusFilter(s); setPage(1); setOpenPanel(null); }}
              className={`text-base px-4 py-2 rounded-full border transition-colors capitalize ${
                statusFilter === s
                  ? 'border-gold text-gold-dark dark:text-gold bg-gold/10'
                  : 'border-royal-300 dark:border-royal-600 text-royal-700 dark:text-royal-200 hover:border-gold/50'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-royal-500 dark:text-royal-200 text-center py-8">Loading...</p>
        ) : moonbeams.length === 0 ? (
          <p className="text-royal-500 dark:text-royal-200 text-center py-8">No Moonbeams yet. Create your first one!</p>
        ) : (
          <div className="space-y-3">
            {moonbeams.map((m) => {
              const linkState = linksByMoonbeam[m.moonbeamid];
              const links = linkState?.links || [];
              const hasLinks = parseInt(m.link_count) > 0;

              return (
                <div key={m.moonbeamid}>
                  <div className="bg-royal-900 dark:bg-royal-800/20 border border-royal-700 dark:border-royal-700/40 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2.5 h-2.5 rounded-full ${getStatusColor(m.status)}`} />
                          <span className="text-white font-semibold text-base">{m.templatename}</span>
                          <span className="text-royal-100 text-sm capitalize">({m.status})</span>
                        </div>
                        <p className="text-royal-100 text-base mt-1">
                          {m.status === 'shared'
                            ? <>Link-only · {m.categoryname}</>
                            : <>To: {m.recipientname || 'Not set'} &middot; {m.categoryname}</>}
                        </p>
                        <p className="text-royal-100 text-sm mt-1">
                          {dateLabel(m)}
                          {m.viewcount > 0 && ` · ${m.viewcount} views`}
                          <ReactionsInlineSummary reactionCount={m.reaction_count} />
                        </p>

                        {/* Progress for scheduled/birthday cards */}
                        {m.status === 'scheduled' && (
                          <DeliveryProgress
                            sentCount={m.sent_count}
                            recipientCount={m.recipient_count}
                            deliveryMode={m.deliverymode}
                          />
                        )}
                      </div>

                      <div className="flex gap-3 items-center flex-shrink-0">
                        {canShowViewButton(m.status) && m.status !== 'shared' && (
                          <button
                            onClick={() => toggleViewPanel(m.moonbeamid)}
                            className={`text-sm flex items-center gap-1 transition-colors ${
                              isViewPanelOpen(m.moonbeamid)
                                ? 'text-gold'
                                : 'text-gold hover:text-gold-light'
                            }`}
                          >
                            View
                            <svg
                              className={`w-3.5 h-3.5 transition-transform ${isViewPanelOpen(m.moonbeamid) ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(m.moonbeamid)}
                          className="text-sm text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* ─── Shareable links section ───────────────────── */}
                    {hasLinks && (
                      <div className="mt-3 pt-3 border-t border-royal-700/40">
                        <p className="text-royal-100 text-sm mb-2 flex items-center gap-1.5">
                          <span aria-hidden="true">🔗</span>
                          <span>
                            Shareable link{links.length !== 1 ? 's' : ''}
                            {linkState?.loading && <span className="text-royal-100 italic ml-1.5">loading...</span>}
                            {!linkState?.loading && ` (${links.length})`}
                          </span>
                        </p>
                        {linkState?.error && (
                          <p className="text-red-400 text-sm">{linkState.error}</p>
                        )}
                        {!linkState?.loading && links.length > 0 && (
                          <div className="space-y-1.5">
                            {links.map((link) => (
                              <ShareableLinkRow
                                key={link.linkid}
                                link={link}
                                onUpdated={(updated) => handleLinkUpdated(m.moonbeamid, updated)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expandable View Recipients panel */}
                  {isViewPanelOpen(m.moonbeamid) && (
                    <ViewRecipientsPanel
                      moonbeamId={m.moonbeamid}
                      uuid={m.uuid}
                      onClose={() => setOpenPanel(null)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Share-success modal — explicit, close-only. Rendered last so it
          overlays the page. Shows after a share completes. */}
      {shareSuccess && (
        <ShareSuccessModal
          shareSuccess={shareSuccess}
          onClose={() => setShareSuccess(null)}
        />
      )}
    </div>
  );
}